import React, {useContext, useEffect, useState} from 'react';
import {Box, Slider, Typography, List, ListItem, ListItemText, Collapse, IconButton, CircularProgress} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {colorPaper, colorTextInactive} from "../App/theme";
import { isMobile } from "../App/utils";
import { ChatModels } from "../constants";
import { AlertContext } from "../App/App";
import { useInputValue } from "../Context/ChatInputProvider";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Close";
import {formatMoneyToString, formatScore, toYAML} from "../utils";
import {ArrowDownward, ArrowDropDown, ArrowDropUp, Edit} from "@mui/icons-material";
import {fetchContextItems} from "./ContextItemsUtils";

const ContextItemsList = React.memo(({ contextItems }) => {
    return (
        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {contextItems.map(({ item, meta }) => {
                const itemString = toYAML({  _score: formatScore(meta.score), _tokens: meta.estimatedTokens, ...item });
                return (
                    <ListItem key={item.itemId} fontSize={14} style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <IconButton size="small" style={{ marginTop: '16px' }} onClick={() => window.open(`/edit-item/${meta.itemId}`, '_blank')}>
                            <Edit fontSize="small" />
                        </IconButton>
                        <ListItemText primary={<pre>{itemString}</pre>} style={{ marginLeft: 8 }} />
                    </ListItem>
                );
            })}
        </List>
    );
});

export const ContextItems = ({ contextItems, setContextItems, contextWindow, setContextWindow, messages, KB,
                                 isContextItemsOpen, setIsContextItemsOpen, totalTokensSum, setTotalTokensSum
                             }) => {

    const [panelWidth, setPanelWidth] = useState(100);
    const { setSystemAlert } = useContext(AlertContext);
    const { inputValue, setInputValue } = useInputValue();
    const [loading, setLoading] = useState(false);

    const handleTokenLimitChange = (event, newValue) => setContextWindow(newValue);

    const togglePanel = () => {
        setIsContextItemsOpen(!isContextItemsOpen);
        if (isContextItemsOpen) {
            setTimeout(() => setPanelWidth(100), 300);
        } else {
            setPanelWidth(isMobile ? 300 : 800);
        }
    };

    useEffect(() => {
        if (isContextItemsOpen) {
            setPanelWidth(isMobile ? 300 : 800);
        }
    }, [isContextItemsOpen]);

    const refreshResults = async () => {
        try {
            setLoading(true)
            const {totalTokens, items} = await fetchContextItems({inputValue, messages, KB, contextWindow})
            setTotalTokensSum(totalTokens)
            setContextItems(items)
            setLoading(false)
        } catch (e) {
            console.error('Unable to fetchContextItems:', e)
            setLoading(false)
        }
    };

    useEffect(() => {
        if (!inputValue && !messages?.length) return setContextItems([])
        let timeout;
        if (isContextItemsOpen) {
            timeout = setTimeout(refreshResults, 500);
        }
        return () => clearTimeout(timeout);
    }, [inputValue, isContextItemsOpen]);

    const maxContextSize = Math.round(ChatModels?.[KB?.model]?.context * 0.8) || 8000;
    const defaultContextWindow = 8000 // Math.round(ChatModels?.[KB?.model]?.context * 0.2) || 8000;

    useEffect(() => {
        if (!KB?.model) return;

        if (contextWindow > maxContextSize) {
            setSystemAlert({ msg: `Max context items reset to ${defaultContextWindow} tokens`, type: 'info', duration: 5000 });
            setContextWindow(defaultContextWindow);
        }

        if (contextWindow === null) {
            setContextWindow(defaultContextWindow);
        }
    }, [messages, contextWindow, KB, inputValue]);

    if (!contextItems) return null;

    return (
        <Box>
            <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={togglePanel}>
                <Typography fontSize={14}>
                    {contextItems.length} context items
                </Typography>
                {/*<IconButton onClick={togglePanel}>*/}
                {/*    {isContextItemsOpen ? <ArrowDropDown /> : <ArrowDropUp />}*/}
                {/*</IconButton>*/}
            </Box>
            <Collapse in={isContextItemsOpen}>
                <Box mt={2} bgcolor={colorPaper} p={2}
                     sx={{ width: panelWidth, maxHeight: 500 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <IconButton onClick={refreshResults}>
                            {loading ? <CircularProgress size={24} /> : <SearchIcon  />}
                        </IconButton>

                        <Typography fontSize={14} sx={{ width: '100%', color: colorTextInactive }}>
                            {contextItems?.length} items visible ({formatMoneyToString(totalTokensSum)} tokens)
                        </Typography>

                        <Typography fontSize={14} sx={{ width: '100%', color: colorTextInactive }}>

                        </Typography>


                        <IconButton onClick={() => togglePanel()}>
                            <CancelIcon />
                        </IconButton>


                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography fontSize={14} sx={{ width: '100%' }}>
                            Maximum Context: <span style={{ display: 'inline-block', minWidth: '50px' }}>{contextWindow}</span> tokens
                        </Typography>
                    </Box>
                    <Slider
                        onMouseUp={refreshResults}
                        value={contextWindow}
                        onChange={handleTokenLimitChange}
                        aria-labelledby="token-slider"
                        min={100}
                        max={maxContextSize}
                        step={100}
                        size={"small"}
                        sx={{ width: 200 }}
                    />
                    <ContextItemsList contextItems={contextItems} />
                </Box>
            </Collapse>
        </Box>
    );
};